import React, { useState } from 'react';
import { proposalService } from '../../services/api';
import { formatDate } from '../../utils/dateFormats';

export default function AdminProposalsTab({ data: proposals, setData: setProposals, districts, actionLoading, setActionLoading, setError }) {
    const [editingProposal, setEditingProposal] = useState(null);
    // Nuevo estado para la creación
    const [isCreating, setIsCreating] = useState(false);
    const [newProposal, setNewProposal] = useState({ title: '', summary: '', district: '', category: '', votes: 0 });

    const handleEdit = (proposal) => {
        setEditingProposal(proposal);
        setIsCreating(false); // Cerrar creación si se va a editar
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (actionLoading || !editingProposal) return;
        setActionLoading(true);
        setError(null);

        try {
            const result = await proposalService.updateProposal(editingProposal.id, editingProposal);
            setProposals(prev => prev.map(p => p.id === editingProposal.id ? result.data || result : p));
            setEditingProposal(null);
        } catch (err) {
            setError('Error al actualizar la Propuesta.');
        } finally {
            setActionLoading(false);
        }
    };

    // --- NUEVA FUNCIÓN: handleCreate ---
    const handleCreate = async (e) => {
        e.preventDefault();
        if (actionLoading) return;
        setActionLoading(true);
        setError(null);

        try {
            const result = await proposalService.createProposal(newProposal);
            // Agregamos la nueva propuesta al inicio de la lista
            setProposals(prev => [result.data || result, ...prev]);
            setNewProposal({ title: '', summary: '', category: '', district: '', votes: 0 });
            setIsCreating(false);
        } catch (err) {
            setError('Error al crear la Propuesta.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (actionLoading) return;
        if (!window.confirm('¿Confirmas la inactivación de esta Propuesta?')) return;
        
        setActionLoading(true);
        setError(null);
        try {
            await proposalService.deleteProposal(id); 
            setProposals(prev => prev.filter(p => p.id !== id));
        } catch (err) {
            setError('Error al inactivar la Propuesta.');
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div className="mt-4">
            <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-semibold">Gestión de Propuestas ({proposals.length})</h4>
                <button 
                    onClick={() => { setIsCreating(!isCreating); setEditingProposal(null); }}
                    className="btn btn-primary text-sm"
                >
                    {isCreating ? 'Cancelar' : '+ Nueva Propuesta'}
                </button>
            </div>

            {/* FORMULARIO DE CREACIÓN */}
            {isCreating && (
                <form onSubmit={handleCreate} className="mb-6 p-4 border rounded bg-green-50 space-y-3 max-w-2xl">
                    <h5 className="font-bold text-green-700">Crear Nueva Propuesta</h5>
                    <input 
                        name="title" type="text" placeholder="Título" 
                        value={newProposal.title} 
                        onChange={(e) => setNewProposal({...newProposal, title: e.target.value})} 
                        required className="input-field" disabled={actionLoading} 
                    />
                    <textarea 
                        name="summary" placeholder="Descripción" 
                        value={newProposal.summary} 
                        onChange={(e) => setNewProposal({...newProposal, summary: e.target.value})} 
                        rows="3" required className="input-field" disabled={actionLoading} 
                    />
                    <input 
                        name="category" placeholder="Categoría" 
                        value={newProposal.category} 
                        onChange={(e) => setNewProposal({...newProposal, category: e.target.value})} 
                        rows="3" required className="input-field" disabled={actionLoading} 
                    />
                    <select 
                        name="district" value={newProposal.district} 
                        onChange={(e) => setNewProposal({...newProposal, district: e.target.value})} 
                        className="input-field" disabled={actionLoading}
                    >
                        <option value="">Seleccione Distrito</option>
                        {(districts || []).map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                    <button type="submit" className="btn btn-primary w-full" disabled={actionLoading}>
                        {actionLoading ? 'Creando...' : 'Publicar Propuesta'}
                    </button>
                </form>
            )}

            {/* FORMULARIO DE EDICIÓN */}
            {editingProposal && (
                <form onSubmit={handleUpdate} className="mb-6 p-4 border rounded bg-yellow-50 space-y-3 max-w-2xl">
                    <h5 className="font-bold">Editar Propuesta ID: {editingProposal.id}</h5>
                    <input 
                        name="title" type="text" value={editingProposal.title} 
                        onChange={(e) => setEditingProposal({...editingProposal, title: e.target.value})} 
                        required className="input-field" disabled={actionLoading} 
                    />
                    <textarea 
                        name="summary" value={editingProposal.summary} 
                        onChange={(e) => setEditingProposal({...editingProposal, summary: e.target.value})} 
                        rows="4" required className="input-field" disabled={actionLoading} 
                    />
                    <select 
                        name="district" value={editingProposal.district} 
                        onChange={(e) => setEditingProposal({...editingProposal, district: e.target.value})} 
                        className="input-field" disabled={actionLoading}
                    >
                        {(districts || []).map(d => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                        ))}
                    </select>
                    <div className="flex space-x-2">
                        <button type="submit" className="btn btn-primary" disabled={actionLoading}>Guardar</button>
                        <button type="button" onClick={() => setEditingProposal(null)} className="btn btn-secondary">Cancelar</button>
                    </div>
                </form>
            )}

            {/* TABLA DE PROPUSTAS */}
            <div className="overflow-x-auto shadow rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="table-th text-left p-3">Título</th>
                            <th className="table-th text-left p-3">Distrito</th>
                            <th className="table-th text-left p-3">Fecha</th>
                            <th className="table-th text-left p-3">Acciones</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {proposals.map((p) => (
                            <tr key={p.id} className="hover:bg-gray-50">
                                <td className="p-3 max-w-xs truncate font-medium">{p.title}</td>
                                <td className="p-3">{(districts || []).find(d => d.id === p.district)?.name || 'N/A'}</td>
                                <td className="p-3 text-gray-500">{formatDate(p.createdAt)}</td>
                                <td className="p-3 space-x-4">
                                    <button onClick={() => handleEdit(p)} className="text-blue-600 hover:text-blue-800 font-medium">Editar</button>
                                    <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-800 font-medium">Eliminar</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}